import type { CachedRecognition } from 'services/home-feed.service';
import { COIN_KEYS, COIN_TIERS, isCoinKey } from 'pages/Reconhecer/recognition-points';
import { GetBehaviorLabel } from 'utils/strings';

import type { RecentRecognition } from './recent-recognitions';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const LOWERCASE_CONNECTORS = new Set(['de', 'da', 'do', 'das', 'dos', 'e']);

/** "há 4 min", "há 2 h", "ontem", "há 3 dias" (FEED-12). */
export function relativeTime(time: number | string, now: number = Date.now()) {
  const elapsed = now - new Date(time).getTime();

  if (elapsed < MINUTE) return 'agora';
  if (elapsed < HOUR) return `há ${Math.floor(elapsed / MINUTE)} min`;
  if (elapsed < DAY) return `há ${Math.floor(elapsed / HOUR)} h`;
  if (elapsed < 2 * DAY) return 'ontem';
  return `há ${Math.floor(elapsed / DAY)} dias`;
}

/** O `nome_guerra` vem em caixa alta: "MARIA DE FATIMA" → "Maria de Fatima". */
export function formatName(name: string) {
  return name
    .trim()
    .toLocaleLowerCase('pt-BR')
    .split(/\s+/)
    .filter(Boolean)
    .map(word => (LOWERCASE_CONNECTORS.has(word) ? word : word.charAt(0).toLocaleUpperCase('pt-BR') + word.slice(1)))
    .join(' ');
}

/** Texto da mensagem sem o HTML do editor. O HTML nunca chega ao DOM da página. */
export function plainText(html: string) {
  const document = new DOMParser().parseFromString(html, 'text/html');
  document.querySelectorAll('script, style').forEach(node => node.remove());
  // Cada bloco vira espaço, senão "<p>a</p><p>b</p>" viraria "ab".
  document.querySelectorAll('p, br, li, div').forEach(node => node.append(' '));
  return (document.body.textContent || '').replace(/\s+/g, ' ').trim();
}

/**
 * Nível do reconhecimento ("#UAU"): pela moeda gravada; sem ela (cache antigo), pela faixa de
 * pontos que a contém; fora de toda faixa, nenhum (HOME-19).
 */
export function recognitionLevel(coin: string | undefined, points: number): string | undefined {
  if (isCoinKey(coin)) return COIN_TIERS[coin].label;

  const tier = COIN_KEYS.find(key => points >= COIN_TIERS[key].min && points <= COIN_TIERS[key].max);
  return tier ? COIN_TIERS[tier].label : undefined;
}

export function toRecentRecognition(item: CachedRecognition, now: number = Date.now()): RecentRecognition {
  const authorFirstName = formatName(item.author || '').split(' ')[0];

  return {
    id: item._id,
    author: authorFirstName || 'Um colega',
    recipient: formatName(item.recipient || '') || 'um colega',
    when: relativeTime(item.time, now),
    message: plainText(item.message || ''),
    behavior: GetBehaviorLabel(item.behavior),
    level: recognitionLevel(item.coin, item.points),
    points: item.points,
    authorImage: item.authorImage ?? undefined,
    authorAvatar: item.authorAvatar ?? undefined,
    authorAvatarType: item.authorAvatarType ?? undefined,
    recipientImage: item.recipientImage ?? undefined,
    recipientAvatar: item.recipientAvatar ?? undefined,
    recipientAvatarType: item.recipientAvatarType ?? undefined,
  };
}
